import { post } from "@readai/common/api";
import type { EntityAccessLevel } from "@readai/common/types";
import { isResponseErrorWithStatus } from "@readai/common/utils";

interface RequestAccessResult {
  success: true;
  status: "hasAccess" | "pending";
}

interface RequestTopicAccessRequest {
  reportType: "topic";
  requesterId: string;
  ownerId: string;
}
interface RequestSessionAccessRequest {
  reportType: "meeting";
  accessLevel?: Omit<EntityAccessLevel, "owner" | "forbidden" | "no_access">;
}

type RequestAccessRequest = { message: string; reportId: string } & (
  | RequestTopicAccessRequest
  | RequestSessionAccessRequest
);

export const requestAccess = async (
  request: RequestAccessRequest,
): Promise<RequestAccessResult> => {
  const reportId = request.reportId;
  const message = request.message;
  try {
    if (request.reportType === "topic") {
      await post(
        `/topics/${reportId}/access-requests`,
        {
          topicReportRequesterId: request.requesterId,
          message,
          topicReportOwnerId: request.ownerId,
        },
        { withCreds: true, parseErrorResponse: true },
      );
    } else {
      await post(
        `/sessions/${reportId}/acl/access-requests`,
        { message, accessLevel: request.accessLevel },
        { withCreds: true, parseErrorResponse: true },
      );
    }

    return { status: "pending", success: true };
  } catch (error) {
    if (isResponseErrorWithStatus(error, 400)) {
      const errorBody = await error.response.json();
      if (errorBody.detail?.includes("already has access")) {
        return { status: "hasAccess", success: true };
      }
    }
    throw error;
  }
};

export const acceptInvite = async (
  reportId: string,
  token: string,
  reportType: "topic" | "meeting",
): Promise<void> => {
  let url;

  if (reportType === "topic") {
    url = `/topics/${reportId}/impersonation/accept-invite`;
  } else {
    url = `/sessions/${reportId}/acl/accept-access-invite`;
  }

  return post(url, { token }, { withCreds: true });
};
